import React, { useContext, useState } from "react";
import {
    Box,
    CssBaseline,
    Grid,
    Typography,
    TextField,
    Button,
    Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { MailOutline, Send, ArrowBack } from "@mui/icons-material";
import { Link } from "react-router-dom";
import i18n from "../../../utilities/i18n";
import Notify from "../../commons/notify";
import Label from "../../commons/Label";
import service from "../service";
import LoginHeader from "../Components/LoginHeader";
import LoadingContext from "../../../container/loadingContext";

// --- Styled Components ---

const RootContainer = styled(Grid)(() => ({
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(159deg, #E6F1FD 0%, #E6F1FD 100%)',
    backgroundAttachment: 'fixed',
}));

const CardContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    maxWidth: 350,
    padding: theme.spacing(4),
    borderRadius: 24,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(10px)",
    [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(2),
        padding: theme.spacing(3),
    },
}));

const StyledInputIcon = styled(MailOutline)(() => ({
    marginRight: 8,
    color: "#9e9e9e",
}));

const StyledSubmitButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.button?.primary || "#1976d2",
    height: 45,
    marginTop:theme.spacing(4),
    fontWeight: "bold",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    textTransform: "none",
    fontSize: '1rem',
    color: "#ffffff",
    '&:hover': {
        backgroundColor: theme.palette.button?.primary || "#1565c0",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
}));

const StyledBackButton = styled(Button)(() => ({
    fontWeight: "bold",
    fontFamily: "Lato, sans-serif",
    textTransform: "none",
    color: "#5e708e",
    '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
    },
}));

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [resetStatus, setResetStatus] = useState("idle");
    const [showNotify, setShowNotify] = useState("hide");
    const [notifyDescription, setNotifyDescription] = useState("");
    const [notifyType, setNotifyType] = useState("success");
    const [notifyPrimaryText, setNotifyPrimaryText] = useState("");
    
    const setShowLoadingBar = useContext(LoadingContext);

    const closeAlert = () => setResetStatus("idle");

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!email.trim()) {
            setEmailError("Email is required");
            return;
        }
        
        // Basic Regex for email validation
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(email)) {
            setEmailError("Please enter a valid email address");
            return;
        }

        setEmailError("");
        setShowLoadingBar(true);

        service.SendForgotPasswordLink({ username: email })
            .then((response) => {
                setShowLoadingBar(false);
                if (response.status === 200) {
                    setResetStatus("success");
                } else {
                    const errorMsg = response.status === 400 && response.data.username === "invalid_username"
                        ? i18n.t("errorMessages.invalid_username")
                        : i18n.t("errorMessages.email_error");
                    
                    showNotifyWindow("show", "danger", errorMsg);
                    setResetStatus("error");
                }
            })
            .catch(() => {
                setShowLoadingBar(false);
                setResetStatus("error");
            });
    };

    function showNotifyWindow(action, type, desc, age = 3000, primaryText = "") {
        setShowNotify(action);
        setNotifyType(type);
        setNotifyDescription(desc);
        setNotifyPrimaryText(primaryText);
        if (action === "show") {
            setTimeout(() => setShowNotify("hide"), age);
        }
    }

    return (
        <RootContainer container>
            <Notify
                showNotify={showNotify}
                notifyPrimaryText={notifyPrimaryText}
                setShowNotify={setShowNotify}
                notifyDescription={notifyDescription}
                setNotifyType={setNotifyType}
                setNotifyDescription={setNotifyDescription}
                notifyType={notifyType}
            />
            <CssBaseline />
            
            <Grid item xs={12} display="flex" flexDirection="column" alignItems="center">
                <CardContainer>
                    <LoginHeader type="forgot_password" />
                    
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
                        {resetStatus === "success" && (
                            <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }} onClose={closeAlert}>
                                Reset link sent! Please check your email.
                            </Alert>
                        )}
                        {resetStatus === "error" && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={closeAlert}>
                                Account not found or service error.
                            </Alert>
                        )}

                        <Label label="Email Address" fontWeight="bold" color="#576b8d" />
                        <TextField
                            name="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (emailError) setEmailError("");
                            }}
                            placeholder="e.g. name@company.com"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                                startAdornment: <StyledInputIcon />,
                            }}
                            error={!!emailError}
                            helperText={emailError}
                            sx={{ mt: 1, mb: 2 }}
                        />

                        <StyledSubmitButton
                            type="submit"
                            variant="contained"
                            fullWidth
                            startIcon={<Send />}
                            disableRipple
                        >
                            Send Reset Link
                        </StyledSubmitButton>

                        <Box textAlign="center" mt={3}>
                            <StyledBackButton
                                component={Link}
                                to="/"
                                startIcon={<ArrowBack />}
                                disableRipple
                            >
                                Back to Login
                            </StyledBackButton>
                        </Box>
                    </Box>
                </CardContainer>

                <Box mt={4}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "Lato", color: "#5e708e" }}
                    >
                        © 2026 BILLI Medical. All rights reserved.
                    </Typography>
                </Box>
            </Grid>
        </RootContainer>
    );
};

export default ForgotPassword;